import { Button, Card, Flex, Image, Text, useDisclosure } from '@chakra-ui/react';
import Modal from '@core/components/Modal/Modal';
import { commonConfig } from '@core/config';
import { selectCurrentUserId, useAuthStore } from '@waitroom/auth';
import { Provider } from '@waitroom/models';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsIntegrationsEnabled } from '../../../../integrations/hooks/useIsIntegrationsEnabled';
import { CompatibilityTags } from './CompatibilityTags/CompatibilityTags';
import ConnectButton from './ConnectButton/ConnectButton';

export type IntegrationCardProps = {
  provider: Provider;
  category: string;
};
export type CustomIntegrationCardProps = IntegrationCardProps & {
  children: ReactNode;
};

export const CustomIntegrationCard = ({ provider, children }: CustomIntegrationCardProps) => {
  return (
    <Card
      variant="outline"
      border="2px solid"
      borderColor="gray.200"
      rounded="2xl"
      color="gray.900"
      p={[4, 5, 6]}
    >
      <Flex gap={4} alignItems="center">
        <Image
          w="full"
          maxW={14}
          h="auto"
          alt={provider.label}
          src={provider.icon.url}
          rounded="lg"
          backgroundColor={provider.icon.backgroundColor}
        />
        <div>
          <Text m={0} fontSize="xl" fontWeight={800}>
            {provider.label}
          </Text>
        </div>
      </Flex>
      <Text mt={4}>{provider.description}</Text>
      <CompatibilityTags integrations={provider.integrations} mt={4} mb={8} />
      {children}
    </Card>
  );
};

const RequestIntegration = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <Modal.Header>{t('customPlan.unlistedHeader')}</Modal.Header>
        <Modal.Body>
          <Text>{t('customPlan.unlistedDesc')}</Text>
        </Modal.Body>
        <Modal.Footer justifyContent="center">
          <Button
            colorScheme="gray.900"
            size="md"
            as="a"
            target="_blank"
            rel="noreferrer noopener"
            href={`mailto:${commonConfig.company.emails.sales}?subject=${encodeURIComponent(t('customPlan.requestEmailSubjectLine'))}`}
          >
            {t('global.contactSales')}
          </Button>
        </Modal.Footer>
      </Modal>
      <Button
        size="sm"
        fontSize="md"
        variant="outline"
        colorScheme="gray.900"
        w="full"
        mt="auto"
        onClick={onOpen}
      >
        {t('global.requestAccess')}
      </Button>
    </>
  );
};

export const IntegrationCard = ({
  provider,
  category,
  isUnlisted,
}: IntegrationCardProps & { isUnlisted: boolean }) => {
  const { t } = useTranslation();
  const currentUserId = useAuthStore(selectCurrentUserId);
  const { isEnabled, redirectTo } = useIsIntegrationsEnabled();

  const renderIntegrationContent = () => {
    if (isUnlisted) {
      return <RequestIntegration />;
    }

    if (isEnabled) {
      return (
        <ConnectButton
          providerName={provider.name}
          category={category}
          userId={currentUserId ?? ''}
        />
      );
    }

    return (
      <Button
        as={Link}
        to={redirectTo}
        size="sm"
        fontSize="md"
        variant="outline"
        colorScheme="gray.900"
        w="full"
        mt="auto"
      >
        {t('global.connect')}
      </Button>
    );
  };

  return (
    <CustomIntegrationCard provider={provider} category={category}>
      {renderIntegrationContent()}
    </CustomIntegrationCard>
  );
};
